import type { ReactNode } from 'react';
import { formatMoney, isCurrencyCode, type JobDetail } from '@nest-recruit/shared';
import { IcpInterviewPanel } from '../../../icp/components/IcpInterviewPanel';
import { IcpProfileForm, type BudgetNote } from '../../../icp/components/IcpProfileForm';
import { useJevBudget } from '../../api/jev-budget-api';
import { jevBudgetButtonOf } from '../../api/use-jev-budget-gate';
import type { IcpWorkspace } from './use-icp-workspace';

// ADR 0061: the docked panel beside the ranking — the interview, or the editable profile. Nothing is drawn while it is closed.

/** What the salary line of the form may say: the top of the budget, "hidden from you", or that there is none (§L23). */
export function budgetNoteOf(detail: JobDetail): BudgetNote {
  const { salaryBudget } = detail;
  if ((salaryBudget.restrictedFields ?? []).length > 0) return { kind: 'hidden' };
  const currency = salaryBudget.currency;
  if (salaryBudget.maxMinor === null || currency === null || !isCurrencyCode(currency)) return { kind: 'none' };
  return { kind: 'amount', text: formatMoney({ amountMinor: BigInt(salaryBudget.maxMinor), currency }) };
}

export function IcpDock({ detail, workspace }: { readonly detail: JobDetail; readonly workspace: IcpWorkspace }): ReactNode {
  // ADR 0070 stage 2: the job's Jev budget on the buttons that spend (the interview is priced by use, so it is off only when nothing is left).
  const budget = useJevBudget(detail.id).data;
  if (workspace.panel === 'interview') {
    return (
      <IcpInterviewPanel
        detail={detail}
        interview={workspace.interview}
        step={workspace.interviewStep}
        costLabel={workspace.interviewCost}
        budget={jevBudgetButtonOf(budget, 0)}
        onStart={workspace.startInterview}
        onAnswer={workspace.answer}
        onNext={workspace.nextQuestion}
        onDraft={workspace.draftNow}
        onClose={workspace.close}
      />
    );
  }
  if (workspace.panel === 'form') {
    return (
      <IcpProfileForm
        key={workspace.draft?.key ?? 0}
        defaultItems={workspace.formItems}
        notes={workspace.draft?.notes ?? []}
        isRemote={detail.workMode === 'remote'}
        budget={budgetNoteOf(detail)}
        interviewCost={workspace.interviewCost}
        estimate={workspace.estimate}
        saveBudget={(lines) => jevBudgetButtonOf(budget, workspace.saveMicroUsd(lines), { isPriced: true })}
        moreBudget={jevBudgetButtonOf(budget, 0)}
        onChange={workspace.editDraft}
        onSave={workspace.save}
        onMore={workspace.askMore}
        onClose={workspace.close}
      />
    );
  }
  return null;
}
